import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Stack,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Assignment as FeatureIcon,
  Bolt as StoryPointIcon,
  CheckCircle as CompletionIcon,
  OpenInNew as OpenInNewIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  Launch as LaunchIcon,
  CloudSync as WebhookIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { get } from '../../services/apiService';

const DARK_GREEN = '#1D4434';
const ACCENT = '#1D4434';

/**
 * Donut showing completion % with number in center; color by completion (red / amber / green).
 * Same visual language as HIPAA/CUI views.
 */
function CompletionDonut({ percentage = 0, size = 40, thickness = 5 }) {
  const value = Math.min(Math.max(Number(percentage), 0), 100);
  const color = value < 33 ? 'error' : value < 67 ? 'warning' : 'success';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress variant="determinate" value={value} color={color} size={size} thickness={thickness} />
      <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" fontWeight="medium" color="text.primary">
          {Math.round(value)}%
        </Typography>
      </Box>
    </Box>
  );
}

/** Build the ADO work item URL (same convention as HIPAA/CUI Product Engineering view). */
function getAdoUrl(workItemId) {
  if (!workItemId) return '#';
  const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
  const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
  const baseUrl = adoOrg.includes('visualstudio.com')
    ? `https://${adoOrg}`
    : `https://dev.azure.com/${adoOrg}`;
  return `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${workItemId}`;
}

function formatLastUpdated(dateString) {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  }
}

/**
 * SHA > Planning & Progress — ADO Epic progress dashboard.
 * Displays a single-epic rollup, filter controls, and an expandable feature table
 * sourced from the WorkItem collection via /api/sha/planning-progress.
 */
const SHAPlanningProgress = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [features, setFeatures] = useState([]);
  const [filters, setFilters] = useState({ team: '', fixVersion: '', state: '' });
  const [expandedFeatures, setExpandedFeatures] = useState(new Set());
  const [featureChildren, setFeatureChildren] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(new Set());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashboardRes, featuresRes] = await Promise.all([
        get('/api/sha/planning-progress/dashboard'),
        get('/api/sha/planning-progress/features')
      ]);
      if (dashboardRes.success) setDashboard(dashboardRes.data);
      if (featuresRes.success) setFeatures(featuresRes.data || []);
    } catch (err) {
      console.error('Error loading SHA Planning & Progress data:', err);
      setError(err.message || 'Failed to load Planning & Progress data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleFeatureExpand = useCallback(
    async (featureId) => {
      const id = String(featureId);
      setExpandedFeatures((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      if (!featureChildren[id]) {
        setLoadingChildren((prev) => new Set(prev).add(id));
        try {
          const res = await get(`/api/sha/planning-progress/features/${id}/children`);
          setFeatureChildren((prev) => ({ ...prev, [id]: res.data || [] }));
        } catch (err) {
          console.error('Error loading SHA feature children:', err);
          setFeatureChildren((prev) => ({ ...prev, [id]: [] }));
        } finally {
          setLoadingChildren((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      }
    },
    [featureChildren]
  );

  const filteredFeatures = useMemo(() => {
    let out = [...features];
    if (filters.team) out = out.filter((f) => f.team === filters.team);
    if (filters.fixVersion) out = out.filter((f) => f.fixVersion === filters.fixVersion);
    if (filters.state) out = out.filter((f) => f.state === filters.state);
    return out;
  }, [features, filters]);

  const uniqueTeams = useMemo(
    () => [...new Set(features.map((f) => f.team).filter(Boolean))].sort(),
    [features]
  );
  const uniqueFixVersions = useMemo(
    () => [...new Set(features.map((f) => f.fixVersion).filter(Boolean))].sort(),
    [features]
  );
  const uniqueStates = useMemo(
    () => [...new Set(features.map((f) => f.state).filter(Boolean))].sort(),
    [features]
  );

  const completion = dashboard?.avgCompletionRate || 0;
  const totalFeatures = dashboard?.totalFeatures || 0;
  const completedStoryPoints = dashboard?.completedStoryPoints || 0;
  const totalStoryPoints = dashboard?.totalStoryPoints || 0;
  const remainingStoryPoints = Math.max(totalStoryPoints - completedStoryPoints, 0);

  // Completed feature count = features at 100% completion
  const completedFeatures = useMemo(
    () => features.filter((f) => (f.completionRate || 0) >= 100).length,
    [features]
  );

  const epicId = dashboard?.epicId;
  const epicTitle = dashboard?.epicTitle;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Planning &amp; Progress
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Epic:
            </Typography>
            {epicId ? (
              <Box
                component="a"
                href={getAdoUrl(epicId)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                #{epicId} {epicTitle ? `— ${epicTitle}` : ''}
                <LaunchIcon sx={{ fontSize: '0.9rem' }} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                (not configured)
              </Typography>
            )}
          </Stack>
        </Box>
        <Tooltip title="Data flows through the shared ADO webhook pipeline into the common workitems collection — no separate SHA sync is maintained.">
          <Chip
            icon={<WebhookIcon sx={{ fontSize: 16 }} />}
            label={
              dashboard?.lastSync
                ? `Auto-synced from ADO webhooks · updated ${formatLastUpdated(dashboard.lastSync)}`
                : 'Auto-synced from ADO webhooks'
            }
            size="small"
            sx={{
              bgcolor: `${DARK_GREEN}10`,
              color: DARK_GREEN,
              fontWeight: 500,
              '& .MuiChip-icon': { color: DARK_GREEN }
            }}
          />
        </Tooltip>
      </Box>

      {/* Rollup Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${ACCENT}08 0%, ${ACCENT}02 100%)`,
              border: `1px solid ${ACCENT}30`
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: `${ACCENT}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CompletionIcon sx={{ color: ACCENT, fontSize: 24 }} />
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                    Overall Completion
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight={700}
                    sx={{
                      color: ACCENT,
                      lineHeight: 1.1,
                      background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}cc 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {completion}%
                  </Typography>
                </Box>
                <CompletionDonut percentage={completion} size={56} thickness={4} />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completion}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: `${ACCENT}15`,
                  '& .MuiLinearProgress-bar': { bgcolor: ACCENT }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Weighted by story points across {totalFeatures} feature{totalFeatures === 1 ? '' : 's'}.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: '#1976d215',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FeatureIcon sx={{ color: '#1976d2', fontSize: 22 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Features
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700}>
                {completedFeatures}
                <Typography component="span" variant="h5" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {' '}/ {totalFeatures}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                completed / total
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: '#ed6c0215',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <StoryPointIcon sx={{ color: '#ed6c02', fontSize: 22 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Story Points
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700}>
                {completedStoryPoints}
                <Typography component="span" variant="h5" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {' '}/ {totalStoryPoints}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {remainingStoryPoints} remaining
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 1 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Team</InputLabel>
              <Select
                value={filters.team}
                onChange={(e) => setFilters({ ...filters, team: e.target.value })}
                label="Team"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueTeams.map((team) => (
                  <MenuItem key={team} value={team}>{team}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Fix Version</InputLabel>
              <Select
                value={filters.fixVersion}
                onChange={(e) => setFilters({ ...filters, fixVersion: e.target.value })}
                label="Fix Version"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueFixVersions.map((v) => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                label="State"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueStates.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Features Table */}
      <TableContainer
        component={Paper}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          '& .MuiTable-root': { minWidth: 650 }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48, paddingRight: 0 }} />
              <TableCell>ADO ID</TableCell>
              <TableCell>Feature Title</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Team</TableCell>
              <TableCell align="right">Story Points</TableCell>
              <TableCell align="right">Completion</TableCell>
              <TableCell>Target Date</TableCell>
              <TableCell>Fix Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFeatures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {features.length === 0
                      ? 'No features found. Try refreshing from Work Items to populate the hierarchy.'
                      : 'No features match the current filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredFeatures.map((feature) => {
                const featureIdStr = String(feature.featureId || '');
                const isExpanded = expandedFeatures.has(featureIdStr);
                const children = featureChildren[featureIdStr];
                const isLoadingChildren = loadingChildren.has(featureIdStr);
                return (
                  <React.Fragment key={`${feature.epicId}-${feature.featureId}`}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => toggleFeatureExpand(feature.featureId)}
                    >
                      <TableCell
                        sx={{ width: 48, paddingRight: 0, verticalAlign: 'middle' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFeatureExpand(feature.featureId);
                          }}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isLoadingChildren ? (
                            <CircularProgress size={20} />
                          ) : isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ChevronRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {feature.featureId ? (
                          <Box
                            component="a"
                            href={getAdoUrl(feature.featureId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontWeight: 500,
                              '&:hover': { textDecoration: 'underline', color: 'primary.dark' }
                            }}
                          >
                            {feature.featureId}
                            <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={feature.featureTitle || ''}>
                          <Typography variant="body2" sx={{ maxWidth: 420 }}>
                            {feature.featureTitle}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {feature.state && (
                          <Chip
                            label={feature.state}
                            size="small"
                            variant="outlined"
                            color={
                              ['Closed', 'Done', 'Completed'].includes(feature.state)
                                ? 'success'
                                : ['Removed'].includes(feature.state)
                                  ? 'default'
                                  : ['Active', 'In Progress', 'Committed'].includes(feature.state)
                                    ? 'primary'
                                    : 'default'
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>{feature.team || '-'}</TableCell>
                      <TableCell align="right">
                        {feature.completedStoryPoints} / {feature.totalStoryPoints}
                      </TableCell>
                      <TableCell align="right">
                        <CompletionDonut percentage={feature.completionRate} />
                      </TableCell>
                      <TableCell>
                        {feature.targetDate ? format(new Date(feature.targetDate), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell>{feature.fixVersion || '-'}</TableCell>
                    </TableRow>
                    {isExpanded && children && children.length > 0 &&
                      children.map((child) => (
                        <TableRow
                          key={child.id}
                          hover
                          sx={{
                            backgroundColor: 'grey.50',
                            '&:hover': { backgroundColor: 'grey.100' }
                          }}
                        >
                          <TableCell sx={{ width: 48, paddingRight: 0, paddingLeft: 2 }} />
                          <TableCell>
                            <Box
                              component="a"
                              href={getAdoUrl(child.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'primary.main',
                                textDecoration: 'none',
                                fontWeight: 500,
                                pl: 3,
                                '&:hover': { textDecoration: 'underline', color: 'primary.dark' }
                              }}
                            >
                              {child.id}
                              <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ pl: 3 }}>
                            <Typography variant="body2">{child.title}</Typography>
                            {child.workItemType && (
                              <Chip
                                label={child.workItemType}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {child.state && (
                              <Chip label={child.state} size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>{feature.team || '-'}</TableCell>
                          <TableCell align="right">
                            {child.completedStoryPoints} / {child.totalStoryPoints}
                          </TableCell>
                          <TableCell align="right">
                            <CompletionDonut percentage={child.completionRate} />
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>{feature.fixVersion || '-'}</TableCell>
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SHAPlanningProgress;
